import {
  chromaOf,
  deltaE2000,
  hueAngleOf,
  labFromLch,
  type ColorRecord,
  type LAB,
} from "./color-engine";

export interface SixtyThirtyTenPalette {
  base: ColorRecord; // 60% — the color the user picked
  structure: ColorRecord; // 30% — tonal support color
  focus: ColorRecord; // 10% — accent/pop color
}

/**
 * Derives a 60:30:10 palette from a single chosen Base color, by
 * constructing synthetic "ideal" Lab targets for Structure and Focus and
 * finding the closest real catalog colors to each.
 *
 * Design rules (deliberately simple and explainable, not a black box):
 *  - Structure is a tonal shift of Base: same hue, slightly less
 *    saturated, moved toward the opposite end of the lightness scale by a
 *    fixed amount. This is the "supporting neutral" — trim, cabinetry,
 *    secondary surfaces — that reads as related to Base but gives
 *    contrast.
 *  - Focus is a genuine accent: complementary hue (opposite side of the
 *    color wheel from Base), boosted chroma, and a fixed mid-dark
 *    lightness — the "pop" color for a single accent wall or detail.
 *
 * Both targets are matched against the real catalog via CIEDE2000, so the
 * result is always an actual product color, never a synthetic one that
 * doesn't correspond to a real shade.
 */
export function generateSixtyThirtyTenPalette(
  base: ColorRecord,
  catalog: ColorRecord[]
): SixtyThirtyTenPalette {
  const baseHue = hueAngleOf(base.lab);
  const baseChroma = chromaOf(base.lab);

  const structureTarget: LAB = labFromLch(
    clamp(base.lab.l > 55 ? base.lab.l - 22 : base.lab.l + 22, 8, 95),
    baseHue, // same hue as Base — tonal, not contrasting hue
    baseChroma * 0.85
  );

  const structure = closestExcluding(structureTarget, catalog, [base.id]);

  const focusTarget: LAB = labFromLch(
    32, // fixed mid-dark lightness — accents read as grounding, not pastel
    (baseHue + 180) % 360, // complementary hue
    Math.min(baseChroma * 1.4 + 8, 40) // bolder than Base, capped to what
    // the curated neutral palette can actually offer — see
    // REVISED_SPEC.md 3a, this app's families are architectural
    // neutrals, not a full saturated color wheel.
  );

  const focus = closestExcluding(focusTarget, catalog, [base.id, structure.id]);

  return { base, structure, focus };
}

function closestExcluding(
  target: LAB,
  catalog: ColorRecord[],
  excludeIds: string[]
): ColorRecord {
  const candidates = catalog.filter((c) => !excludeIds.includes(c.id));
  if (candidates.length === 0) {
    throw new Error("No catalog colors available to build a palette from.");
  }

  let best = candidates[0];
  let bestDeltaE = deltaE2000(target, best.lab);

  for (const candidate of candidates.slice(1)) {
    const d = deltaE2000(target, candidate.lab);
    if (d < bestDeltaE) {
      best = candidate;
      bestDeltaE = d;
    }
  }

  return best;
}

function clamp(v: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, v));
}
